import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRightOutlined,
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Alert, Flex, Form, Modal, Radio, Select, Tag, Typography } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { isCrossSiteWorkOrderMove } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

export type MoveTicketConfirmPayload =
  | { mode: 'existing'; targetWorkOrderId: string }
  | { mode: 'createNew' };

export type MoveTargetOption = {
  value: string;
  label: string;
  code?: string;
  disabled?: boolean;
  siteId?: string | null;
  siteName?: string | null;
};

export interface MoveTicketDialogProps {
  open: boolean;
  ticketCode: string;
  sourceWorkOrderCode?: string;
  sourceSiteId?: string | null;
  sourceSiteName?: string | null;
  /** When set, skip the picker and ask for explicit confirmation. */
  targetWorkOrderCode?: string;
  targetSiteId?: string | null;
  targetSiteName?: string | null;
  targetOptions: MoveTargetOption[];
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (payload: MoveTicketConfirmPayload) => void;
  /** Required in confirm mode (drag-drop). */
  preselectedTargetWorkOrderId?: string;
  /** Open directly in "create new WO" confirmation. */
  createNewWorkOrder?: boolean;
}

type MoveFormValues = {
  mode: 'existing' | 'createNew';
  targetWorkOrderId?: string;
};

export const MoveTicketDialog: FC<MoveTicketDialogProps> = ({
  open,
  ticketCode,
  sourceWorkOrderCode,
  sourceSiteId,
  sourceSiteName,
  targetWorkOrderCode,
  targetSiteId,
  targetSiteName,
  targetOptions,
  loading = false,
  onCancel,
  onConfirm,
  preselectedTargetWorkOrderId,
  createNewWorkOrder = false,
}) => {
  const { t } = useTranslation('maintenance');
  const [form] = Form.useForm<MoveFormValues>();
  const isConfirmMode = Boolean(preselectedTargetWorkOrderId);
  const isCreateNewMode = createNewWorkOrder && !isConfirmMode;
  const [mode, setMode] = useState<'existing' | 'createNew'>('existing');
  const selectedTargetId = Form.useWatch('targetWorkOrderId', form);

  useEffect(() => {
    if (!open) return;
    const nextMode = isCreateNewMode ? 'createNew' : 'existing';
    setMode(nextMode);
    form.setFieldsValue({
      mode: nextMode,
      targetWorkOrderId: undefined,
    });
  }, [open, isCreateNewMode, form]);

  const pickerTarget = useMemo(() => {
    if (!selectedTargetId) return null;
    return targetOptions.find((opt) => opt.value === selectedTargetId) ?? null;
  }, [selectedTargetId, targetOptions]);

  const crossSite = isConfirmMode
    ? isCrossSiteWorkOrderMove(sourceSiteId, targetSiteId)
    : mode === 'existing' &&
      isCrossSiteWorkOrderMove(sourceSiteId, pickerTarget?.siteId);

  const resolvedTargetSiteName = isConfirmMode
    ? targetSiteName
    : (pickerTarget?.siteName ?? null);

  const titleText = isConfirmMode
    ? t('workOrders.kanban.move.confirmTitle')
    : isCreateNewMode || mode === 'createNew'
      ? t('workOrders.kanban.move.createNewTitle', { code: ticketCode })
      : t('workOrders.kanban.move.title', { code: ticketCode });

  const okText =
    isCreateNewMode || (!isConfirmMode && mode === 'createNew')
      ? t('workOrders.kanban.move.createNewConfirm')
      : crossSite
        ? t('workOrders.kanban.move.crossSiteOk')
        : t('workOrders.kanban.move.confirm');

  const sourceSiteLabel = sourceSiteName ?? sourceSiteId ?? '—';
  const targetSiteLabel = resolvedTargetSiteName ?? pickerTarget?.siteId ?? '—';
  const targetWoLabel = isConfirmMode
    ? (targetWorkOrderCode ?? '—')
    : (pickerTarget?.code ?? '—');

  const crossSiteAlert = crossSite ? (
    <Alert
      type="warning"
      showIcon
      title={t('workOrders.kanban.move.crossSiteTitle')}
      description={
        <Trans
          i18nKey="workOrders.kanban.move.crossSiteBody"
          ns="maintenance"
          values={{
            ticket: ticketCode,
            from: sourceWorkOrderCode ?? '—',
            to: targetWoLabel,
            sourceSite: sourceSiteLabel,
            targetSite: targetSiteLabel,
          }}
          components={{ bold: <strong /> }}
        />
      }
    />
  ) : null;

  return (
    <Modal
      open={open}
      centered
      width={480}
      title={
        <Flex align="center" gap={10}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background:
                isCreateNewMode || mode === 'createNew' ? '#ecfdf5' : '#e0f2fe',
              color:
                isCreateNewMode || mode === 'createNew'
                  ? '#047857'
                  : WORK_ORDER_UI.suggestionLink,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isCreateNewMode || mode === 'createNew' ? (
              <PlusOutlined style={{ fontSize: 18 }} />
            ) : (
              <SwapOutlined style={{ fontSize: 18 }} />
            )}
          </span>
          <div>
            <Typography.Text strong style={{ display: 'block', fontSize: 16 }}>
              {titleText}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {isCreateNewMode || mode === 'createNew'
                ? t('workOrders.kanban.move.createNewSubtitle')
                : t('workOrders.kanban.move.subtitle')}
            </Typography.Text>
          </div>
        </Flex>
      }
      okText={okText}
      cancelText={t('workOrders.kanban.cancel')}
      confirmLoading={loading}
      okButtonProps={{
        style: {
          background: WORK_ORDER_UI.primary,
          borderColor: WORK_ORDER_UI.primary,
        },
      }}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        if (isConfirmMode && preselectedTargetWorkOrderId) {
          onConfirm({
            mode: 'existing',
            targetWorkOrderId: preselectedTargetWorkOrderId,
          });
          return;
        }
        if (isCreateNewMode || mode === 'createNew') {
          onConfirm({ mode: 'createNew' });
          form.resetFields();
          return;
        }
        void form.validateFields().then((values) => {
          if (!values.targetWorkOrderId) return;
          onConfirm({
            mode: 'existing',
            targetWorkOrderId: values.targetWorkOrderId,
          });
          form.resetFields();
        });
      }}
      destroyOnHidden
    >
      {isConfirmMode ? (
        <Flex vertical gap={14}>
          <div
            style={{
              background: WORK_ORDER_UI.pageBg,
              border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
              borderRadius: 12,
              padding: 14,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workOrders.kanban.move.ticketLabel')}
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <Tag
                style={{
                  margin: 0,
                  background: '#dbeafe',
                  color: '#1e40af',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {ticketCode}
              </Tag>
            </div>
          </div>

          <Flex align="center" gap={10} wrap="wrap">
            <WoChip
              label={t('workOrders.kanban.move.fromLabel')}
              code={sourceWorkOrderCode ?? '—'}
              siteName={sourceSiteName}
              tone="source"
            />
            <ArrowRightOutlined
              style={{ color: WORK_ORDER_UI.suggestionLink, fontSize: 16 }}
            />
            <WoChip
              label={t('workOrders.kanban.move.toLabel')}
              code={targetWorkOrderCode ?? '—'}
              siteName={targetSiteName}
              tone="target"
            />
          </Flex>

          {crossSiteAlert}

          <Alert
            type="info"
            showIcon
            title={t('workOrders.kanban.move.confirmHint')}
          />
        </Flex>
      ) : isCreateNewMode ? (
        <Flex vertical gap={14}>
          <div
            style={{
              background: WORK_ORDER_UI.pageBg,
              border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
              borderRadius: 12,
              padding: 14,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workOrders.kanban.move.ticketLabel')}
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <Tag
                style={{
                  margin: 0,
                  background: '#dbeafe',
                  color: '#1e40af',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {ticketCode}
              </Tag>
            </div>
          </div>
          <Alert
            type="info"
            showIcon
            title={t('workOrders.kanban.move.createNewHint')}
          />
        </Flex>
      ) : (
        <Form form={form} layout="vertical" style={{ marginTop: 4 }}>
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              background: WORK_ORDER_UI.pageBg,
              borderRadius: 10,
              border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workOrders.kanban.move.ticketLabel')}
            </Typography.Text>
            <div>
              <Typography.Text strong>{ticketCode}</Typography.Text>
            </div>
            {sourceSiteName ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('workOrders.kanban.move.fromSite', {
                  site: sourceSiteName,
                  defaultValue: `From site: ${sourceSiteName}`,
                })}
              </Typography.Text>
            ) : null}
          </div>

          <Form.Item
            name="mode"
            label={t('workOrders.kanban.move.modeLabel')}
            initialValue="existing"
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => setMode(e.target.value)}
              options={[
                {
                  value: 'existing',
                  label: t('workOrders.kanban.move.modeExisting'),
                },
                {
                  value: 'createNew',
                  label: t('workOrders.kanban.move.modeCreateNew'),
                },
              ]}
            />
          </Form.Item>

          {mode === 'existing' ? (
            <>
              <Form.Item
                name="targetWorkOrderId"
                label={t('workOrders.kanban.move.target')}
                rules={[
                  {
                    required: true,
                    message: t('workOrders.kanban.move.targetRequired'),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={targetOptions}
                  placeholder={t('workOrders.kanban.move.targetPlaceholder')}
                  size="large"
                />
              </Form.Item>
              {crossSiteAlert}
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              title={t('workOrders.kanban.move.createNewHint')}
            />
          )}
        </Form>
      )}
    </Modal>
  );
};

function WoChip({
  label,
  code,
  siteName,
  tone,
}: {
  label: string;
  code: string;
  siteName?: string | null;
  tone: 'source' | 'target';
}) {
  const isTarget = tone === 'target';
  return (
    <div
      style={{
        flex: '1 1 140px',
        minWidth: 140,
        background: isTarget ? '#ecfdf5' : '#fff',
        border: `1px solid ${isTarget ? '#a7f3d0' : WORK_ORDER_UI.cardBorder}`,
        borderRadius: 12,
        padding: '10px 12px',
      }}
    >
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {label}
      </Typography.Text>
      <Typography.Text
        strong
        style={{
          display: 'block',
          marginTop: 4,
          color: isTarget ? '#047857' : WORK_ORDER_UI.sidebarNavy,
        }}
      >
        {code}
      </Typography.Text>
      {siteName ? (
        <Typography.Text
          strong
          style={{ display: 'block', marginTop: 2, fontSize: 12 }}
        >
          {siteName}
        </Typography.Text>
      ) : null}
    </div>
  );
}
